import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatUtcDate, personName } from '../lib/format';
import type { AssetDetail, RoomDetail } from '../lib/types';
import { PersonTypeBadge } from './badges';
import { PersonPicker } from './pickers';
import { useToast } from './toast';
import { Button, Card, CardHeader, Field, Input } from './ui';
import { WorkOrderStatusBadge } from './work-order-badges';
import { WorkOrderFormModal } from './WorkOrderFormModal';

/** Work orders on an asset, with a shortcut for technicians to log a new one. */
export function AssetWorkOrdersCard({ asset }: { asset: Pick<AssetDetail, 'id' | 'assetTag' | 'name' | 'modelId' | 'model' | 'workOrders'> }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  // Stable reference so a background refetch doesn't reset an open form.
  const device = useMemo(
    () => ({ id: asset.id, assetTag: asset.assetTag, name: asset.name, modelId: asset.modelId, model: asset.model }),
    [asset.id, asset.assetTag, asset.name, asset.modelId, asset.model],
  );

  return (
    <Card>
      <CardHeader
        title="Work orders"
        actions={
          can('TECHNICIAN') && (
            <Button size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
              New
            </Button>
          )
        }
      />
      {asset.workOrders.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No problems reported for this asset.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {asset.workOrders.map((ticket) => (
            <li key={ticket.id}>
              <Link to={`/work-orders/${ticket.id}`} className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-900">
                    <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {ticket.requesterName} · {formatDate(ticket.createdAt)}
                  </p>
                </div>
                <WorkOrderStatusBadge status={ticket.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <WorkOrderFormModal open={creating} onClose={() => setCreating(false)} device={device} staff onCreated={(ticket) => navigate(`/work-orders/${ticket.id}`)} />
    </Card>
  );
}

/** The day before a YYYY-MM-DD date: the last day of an assignment that ends on that date. */
function dayBefore(iso: string) {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/**
 * People responsible for a room, and who was before them. They can report problems with devices kept in or assigned to
 * the room. Taking someone off ends their responsibility today; the history stays.
 */
export function RoomPeopleCard({
  roomId,
  roomName,
  assignments,
  today,
  closed,
}: {
  roomId: string;
  roomName: string;
  assignments: RoomDetail['assignments'];
  today: string;
  closed?: boolean;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState('');
  const [showPast, setShowPast] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['room', roomId] });
    void queryClient.invalidateQueries({ queryKey: ['room-assignments'] });
  };

  const add = useMutation({
    mutationFn: (personId: string) => api.post(`/rooms/${roomId}/people`, { personId, ...(startDate ? { startDate } : {}) }),
    onSuccess: () => {
      refresh();
      toast.success(startDate && startDate > today ? `Added, starting ${formatUtcDate(startDate)}` : 'Added to this room');
      setStartDate('');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (personId: string) => api.delete(`/rooms/${roomId}/people/${personId}`),
    onSuccess: () => {
      refresh();
      toast.success('Their responsibility for this room ends today');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const current = assignments.filter((row) => row.state === 'CURRENT');
  const upcoming = assignments.filter((row) => row.state === 'UPCOMING');
  const past = assignments.filter((row) => row.state === 'PAST');
  const canWrite = can('TECHNICIAN');

  const row = (assignment: RoomDetail['assignments'][number], when: string, removable: boolean) => (
    <li key={assignment.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
      <Link to={`/people/${assignment.person.id}`} className="min-w-0 flex-1 truncate font-medium text-slate-900 hover:text-brand-700">
        {personName(assignment.person)}
        <span className="block truncate text-xs font-normal text-slate-500">
          {when}
          {assignment.source === 'ROLLOVER' && ' · year-start rollover'}
        </span>
      </Link>
      <PersonTypeBadge type={assignment.person.type} />
      {removable && canWrite && (
        <button
          type="button"
          onClick={() => remove.mutate(assignment.person.id)}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
          aria-label={`Take ${personName(assignment.person)} off this room`}
        >
          <X className="size-4" />
        </button>
      )}
    </li>
  );

  return (
    <Card>
      <CardHeader title="Responsible people" description={`They can report problems with devices assigned to or kept in ${roomName}.`} />
      {current.length === 0 && upcoming.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No one right now.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {current.map((assignment) =>
            row(assignment, `Since ${formatUtcDate(assignment.startDate)}${assignment.endDate ? `, until ${formatUtcDate(dayBefore(assignment.endDate))}` : ''}`, true),
          )}
          {upcoming.map((assignment) => row(assignment, `Starts ${formatUtcDate(assignment.startDate)}`, true))}
        </ul>
      )}
      {past.length > 0 && (
        <div className="border-t border-slate-100">
          <button type="button" onClick={() => setShowPast((value) => !value)} className="w-full px-5 py-2.5 text-left text-xs font-medium text-brand-700 hover:bg-slate-50">
            {showPast ? 'Hide' : 'Show'} who was responsible before ({past.length})
          </button>
          {showPast && (
            <ul className="divide-y divide-slate-100 bg-slate-50/50">
              {past.map((assignment) => row(assignment, `${formatUtcDate(assignment.startDate)} – ${formatUtcDate(dayBefore(assignment.endDate!))}`, false))}
            </ul>
          )}
        </div>
      )}
      {canWrite && !closed && (
        <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 p-4">
          <div className="min-w-60 flex-1">
            <PersonPicker inline value={null} onChange={(person) => person && add.mutate(person.id)} placeholder="Add a teacher or staff member" />
          </div>
          <Field label="Starting" hint="Leave blank for today">
            <Input type="date" value={startDate} min={today} onChange={(e) => setStartDate(e.target.value)} className="w-40" />
          </Field>
        </div>
      )}
    </Card>
  );
}
